<script setup>
import PageGuidance from '@/components/guidance/PageGuidance.vue'
import PageHeader from '@/components/shared/PageHeader.vue'
import AvailabilityCalendar from '@/components/scheduling/AvailabilityCalendar.vue'
import { useAvailabilityStore } from '@/stores/availability'

definePage({
  meta: {
    navActiveLink: 'availability',
  },
})

const store = useAvailabilityStore()
const { t } = useI18n()

const formError = ref('')
const formSuccess = ref('')
const ruleDialog = ref(false)
const oneOffDialog = ref(false)
const deleteRuleDialog = ref(false)
const ruleToDelete = ref(null)
const editingRuleId = ref(null)

const DAY_OPTIONS = [
  { value: 1, titleKey: 'availability.days.mon' },
  { value: 2, titleKey: 'availability.days.tue' },
  { value: 3, titleKey: 'availability.days.wed' },
  { value: 4, titleKey: 'availability.days.thu' },
  { value: 5, titleKey: 'availability.days.fri' },
  { value: 6, titleKey: 'availability.days.sat' },
  { value: 7, titleKey: 'availability.days.sun' },
]

const DURATION_OPTIONS = [30, 45, 60, 90, 120]

const emptyRuleForm = () => ({
  days_of_week: [1, 3, 5],
  starts_time: '06:00',
  ends_time: '09:00',
  slot_duration_minutes: 60,
  active: true,
})

const ruleForm = ref(emptyRuleForm())

const oneOffForm = ref({
  date: '',
  starts_time: '10:00',
  ends_time: '11:00',
})

const localDateKey = isoOrDate => {
  const d = new Date(isoOrDate)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')

  return `${y}-${m}-${day}`
}

const openCreateRule = () => {
  editingRuleId.value = null
  ruleForm.value = emptyRuleForm()
  formError.value = ''
  ruleDialog.value = true
}

const openEditRule = rule => {
  editingRuleId.value = rule.id
  ruleForm.value = {
    days_of_week: [...rule.days_of_week],
    starts_time: rule.starts_time,
    ends_time: rule.ends_time,
    slot_duration_minutes: rule.slot_duration_minutes,
    active: rule.active,
  }
  formError.value = ''
  ruleDialog.value = true
}

const toggleDay = day => {
  const days = ruleForm.value.days_of_week
  const idx = days.indexOf(day)
  if (idx === -1)
    days.push(day)
  else
    days.splice(idx, 1)
  days.sort((a, b) => a - b)
}

const submitRule = async () => {
  formError.value = ''
  formSuccess.value = ''
  if (!ruleForm.value.days_of_week.length) {
    formError.value = t('availability.errors.daysRequired')

    return
  }

  try {
    if (editingRuleId.value)
      await store.updateRecurrenceRule(editingRuleId.value, ruleForm.value)
    else
      await store.createRecurrenceRule(ruleForm.value)

    formSuccess.value = t('availability.ruleSaved')
    ruleDialog.value = false
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const confirmDeleteRule = rule => {
  ruleToDelete.value = rule
  deleteRuleDialog.value = true
}

const submitDeleteRule = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await store.destroyRecurrenceRule(ruleToDelete.value.id)
    formSuccess.value = t('availability.ruleDeleted')
    deleteRuleDialog.value = false
    ruleToDelete.value = null
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const openOneOff = () => {
  const tomorrow = new Date()

  tomorrow.setDate(tomorrow.getDate() + 1)
  oneOffForm.value = {
    date: localDateKey(tomorrow),
    starts_time: '10:00',
    ends_time: '11:00',
  }
  formError.value = ''
  oneOffDialog.value = true
}

const combineLocalIso = (dateStr, timeStr) => {
  const [y, m, d] = dateStr.split('-').map(Number)
  const [hh, mm] = timeStr.split(':').map(Number)
  const local = new Date(y, m - 1, d, hh, mm, 0, 0)

  return local.toISOString()
}

const submitOneOff = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await store.createOneOffSlot({
      starts_at: combineLocalIso(oneOffForm.value.date, oneOffForm.value.starts_time),
      ends_at: combineLocalIso(oneOffForm.value.date, oneOffForm.value.ends_time),
    })
    formSuccess.value = t('availability.oneOffSaved')
    oneOffDialog.value = false
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const dayLabel = dayValue => {
  const opt = DAY_OPTIONS.find(d => d.value === dayValue)

  return opt ? t(opt.titleKey) : String(dayValue)
}

const ruleSummary = rule => {
  const days = rule.days_of_week.map(dayLabel).join(', ')

  return `${days} · ${rule.starts_time}–${rule.ends_time} · ${rule.slot_duration_minutes} min`
}
</script>

<template>
  <div>
    <PageHeader
      :title="$t('availability.title')"
      :subtitle="$t('availability.subtitle')"
    >
      <template #actions>
        <div
          class="d-flex flex-wrap gap-2"
          data-tour="availability-actions"
        >
          <VBtn
            variant="tonal"
            :disabled="store.saving"
            @click="openOneOff"
          >
            {{ $t('availability.addOneOff') }}
          </VBtn>
          <VBtn
            color="primary"
            :disabled="store.saving"
            @click="openCreateRule"
          >
            {{ $t('availability.addPattern') }}
          </VBtn>
        </div>
        <PageGuidance />
      </template>
    </PageHeader>

    <VAlert
      v-if="formSuccess"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ formSuccess }}
    </VAlert>

    <VAlert
      v-if="formError || store.error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ formError || store.error }}
    </VAlert>

    <VCard
      flat
      border
      class="mb-4"
      data-tour="availability-patterns"
    >
      <VCardItem>
        <VCardTitle>{{ $t('availability.patternsTitle') }}</VCardTitle>
        <VCardSubtitle>{{ $t('availability.patternsSubtitle') }}</VCardSubtitle>
      </VCardItem>
      <VCardText>
        <div
          v-if="!store.recurrenceRules.length"
          class="text-medium-emphasis"
          role="status"
        >
          {{ $t('availability.patternsEmpty') }}
        </div>
        <VList
          v-else
          lines="two"
          border
          rounded
        >
          <VListItem
            v-for="rule in store.recurrenceRules"
            :key="rule.id"
            :title="ruleSummary(rule)"
            :subtitle="rule.active ? $t('availability.patternActive') : $t('availability.patternInactive')"
          >
            <template #append>
              <div class="d-flex flex-wrap ga-2">
                <VBtn
                  size="small"
                  variant="tonal"
                  :disabled="store.saving"
                  @click="openEditRule(rule)"
                >
                  {{ $t('availability.edit') }}
                </VBtn>
                <VBtn
                  size="small"
                  color="error"
                  variant="tonal"
                  :disabled="store.saving"
                  @click="confirmDeleteRule(rule)"
                >
                  {{ $t('common.delete') }}
                </VBtn>
              </div>
            </template>
          </VListItem>
        </VList>
      </VCardText>
    </VCard>

    <div data-tour="availability-calendar">
      <AvailabilityCalendar
        v-model:form-error="formError"
        @success="formSuccess = $event"
      />
    </div>

    <!-- Recurrence rule dialog -->
    <VDialog
      v-model="ruleDialog"
      max-width="560"
    >
      <VCard flat>
        <VCardItem>
          <VCardTitle>
            {{ editingRuleId ? $t('availability.editPattern') : $t('availability.addPattern') }}
          </VCardTitle>
          <VCardSubtitle>{{ $t('availability.patternDialogSubtitle') }}</VCardSubtitle>
        </VCardItem>
        <VCardText>
          <VAlert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
            role="alert"
          >
            {{ formError }}
          </VAlert>

          <div
            class="d-flex flex-wrap ga-2 mb-4"
            role="group"
            :aria-label="$t('availability.daysLabel')"
          >
            <VBtn
              v-for="day in DAY_OPTIONS"
              :key="day.value"
              size="small"
              :variant="ruleForm.days_of_week.includes(day.value) ? 'flat' : 'tonal'"
              :color="ruleForm.days_of_week.includes(day.value) ? 'primary' : undefined"
              :aria-pressed="ruleForm.days_of_week.includes(day.value)"
              @click="toggleDay(day.value)"
            >
              {{ $t(day.titleKey) }}
            </VBtn>
          </div>

          <div class="d-flex flex-wrap ga-4 mb-4">
            <VTextField
              v-model="ruleForm.starts_time"
              type="time"
              :label="$t('availability.startsTime')"
              style="max-inline-size: 10rem;"
            />
            <VTextField
              v-model="ruleForm.ends_time"
              type="time"
              :label="$t('availability.endsTime')"
              style="max-inline-size: 10rem;"
            />
            <VSelect
              v-model="ruleForm.slot_duration_minutes"
              :label="$t('availability.duration')"
              :items="DURATION_OPTIONS.map(m => ({ title: `${m} min`, value: m }))"
              style="max-inline-size: 10rem;"
            />
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="ruleDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="store.saving"
            @click="submitRule"
          >
            {{ $t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- One-off dialog -->
    <VDialog
      v-model="oneOffDialog"
      max-width="480"
    >
      <VCard flat>
        <VCardItem>
          <VCardTitle>{{ $t('availability.addOneOff') }}</VCardTitle>
          <VCardSubtitle>{{ $t('availability.oneOffSubtitle') }}</VCardSubtitle>
        </VCardItem>
        <VCardText>
          <VAlert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
            role="alert"
          >
            {{ formError }}
          </VAlert>
          <VTextField
            v-model="oneOffForm.date"
            type="date"
            :label="$t('availability.date')"
            class="mb-4"
          />
          <div class="d-flex flex-wrap ga-4">
            <VTextField
              v-model="oneOffForm.starts_time"
              type="time"
              :label="$t('availability.startsTime')"
            />
            <VTextField
              v-model="oneOffForm.ends_time"
              type="time"
              :label="$t('availability.endsTime')"
            />
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="oneOffDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="store.saving"
            @click="submitOneOff"
          >
            {{ $t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Delete rule confirm -->
    <VDialog
      v-model="deleteRuleDialog"
      max-width="480"
    >
      <VCard flat>
        <VCardItem>
          <VCardTitle>{{ $t('availability.deletePatternTitle') }}</VCardTitle>
        </VCardItem>
        <VCardText>
          {{ $t('availability.deletePatternBody') }}
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="deleteRuleDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="error"
            :loading="store.saving"
            @click="submitDeleteRule"
          >
            {{ $t('common.delete') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
